<template>
  <!-- Progress bars -->
  <div v-if="!readonly">
    <div class="row mb-2">
      <div class="col">
        <label for="orientation" class="form-label mb-2">{{ $t('components.product-parameter.balcony.orientation-type') }}</label
        ><br />
        <el-radio-group v-model="order.orientationType" size="large" id="orientation">
          <el-radio-button v-for="orientationType in Object.values(OrientationType)" :key="orientationType" :label="orientationType" :disabled="readonly">
            {{ $t(`enum.orientationType.${orientationType}`) }} <br />
            <img class="parameter-img" :src="getImageSrc(orientationType)" />
          </el-radio-button>
        </el-radio-group>
      </div>
    </div>

    <div class="row mb-2">
      <div class="col">
        <label for="carrying-out" class="form-label mb-2">{{ $t('components.product-parameter.balcony.carrying-out') }}</label
        ><br />
        <el-radio-group v-model="order.balconyType" size="large" id="carrying-out">
          <el-radio-button
            v-for="balconyType in Object.values(BalconyType)"
            :key="balconyType"
            :label="balconyType"
            @change="$emit('sizeChanged')"
            :disabled="readonly"
          >
            {{ $t(`enum.balconyType.${balconyType}`) }} <br />
            <img class="parameter-img" :src="getImageSrc(balconyType)" />
          </el-radio-button>
        </el-radio-group>
      </div>
    </div>

    <div class="row mb-2">
      <div class="col">
        <label for="paneelType" class="form-label mb-2">{{ $t('components.product-parameter.balcony.paneel-type') }}</label
        ><br />
        <el-radio-group v-model="order.paneelType" size="large" id="paneelType" @change="mountingTypeChanged">
          <el-radio-button
            v-for="paneelType in Object.values(PaneelType)"
            :key="paneelType"
            :label="paneelType"
            @change="$emit('sizeChanged')"
            :disabled="readonly"
          >
            {{ $t(`enum.paneelType.${paneelType}`) }} <br />
            <img class="parameter-img" :src="getImageSrc(paneelType)" />
          </el-radio-button>
        </el-radio-group>
      </div>
    </div>

    <div class="row mb-2">
      <div class="col">
        <label for="mounting" class="form-label mb-2">{{ $t('components.product-parameter.balcony.mounting-type') }}</label
        ><br />
        <el-radio-group v-model="order.mountingType" size="large" id="mounting" @change="mountingTypeChanged">
          <el-radio-button
            v-for="mountingType in Object.values(MountingType)"
            :key="mountingType"
            :label="mountingType"
            :disabled="order?.paneelType == PaneelType.portrait || readonly"
            @change="$emit('sizeChanged')"
          >
            {{ $t(`enum.mountingType.${mountingType}`) }} <br />
            <img class="parameter-img" :src="getImageSrc(mountingType)" />
          </el-radio-button>
        </el-radio-group>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <label for="width-range" class="form-label mb-0">{{ $t('components.order-form.width') }}</label>
        <el-slider
          v-model="order.calfCircumferenceWithoutDeduction"
          show-input
          :precision="0"
          :min="0"
          :max="20000"
          :step="1"
          :show-tooltip="false"
          id="width-range"
          @change="$emit('sizeChanged')"
          :disabled="readonly"
        />
      </div>
    </div>
    <div class="row">
      <div class="col">
        <label for="height-range" class="form-label mb-0">{{ $t('components.order-form.height') }}</label>
        <el-slider
          v-model="order.heightWithoutDeduction"
          show-input
          :precision="0"
          :min="0"
          :max="20000"
          :step="1"
          :show-tooltip="false"
          if="height-range"
          disabled
        />
      </div>
    </div>
    <div class="row" v-if="order?.balconyType == BalconyType.uForm">
      <div class="col">
        <el-switch
          v-model="depthConnected"
          class="mt-2"
          inline-prompt
          :active-icon="Connection"
          :inactive-icon="Close"
          @change="
            (activated: any) => {
              if (activated && order) order.depthRight = order.depth;
            }
          "
        />
      </div>
    </div>
    <div class="row" v-if="order?.balconyType != BalconyType.straight">
      <div class="col">
        <label v-if="order?.balconyType == BalconyType.uForm" for="length-range" class="form-label mb-0">{{
          $t('components.order-form.depth-left')
        }}</label>
        <label v-else class="form-label mb-0">{{ $t('components.order-form.depth') }}</label>
        <el-slider
          v-model="order.depth"
          show-input
          :precision="0"
          :min="0"
          :max="20000"
          :step="1"
          :show-tooltip="false"
          id="length-range"
          @change="
            () => {
              if (depthConnected && order) order.depthRight = order.depth;
              $emit('sizeChanged');
            }
          "
          :disabled="readonly"
        />
      </div>
    </div>
    <div class="row" v-if="order?.balconyType == BalconyType.uForm">
      <div class="col">
        <label for="length-range" class="form-label mb-0">{{ $t('components.order-form.depth-right') }}</label>
        <el-slider
          v-if="depthConnected"
          v-model="order.depth"
          show-input
          :precision="0"
          :min="0"
          :max="20000"
          :step="1"
          :show-tooltip="false"
          id="length-range"
          :disabled="true"
        />
        <el-slider
          v-else
          v-model="order.depthRight"
          show-input
          :precision="0"
          :min="0"
          :max="20000"
          :step="1"
          :show-tooltip="false"
          id="length-range"
          @change="$emit('sizeChanged')"
          :disabled="readonly"
        />
      </div>
    </div>
  </div>
  <div v-else>
    <h3 class="mt-3">{{ $t('components.order-summary.parameterTitle') }}</h3>
    <div class="row1">
      <label class="col-sm-6 form-label text-secondary mb-1">{{ $t('components.product-parameter.balcony.carrying-out') }}</label>
      <label class="col-sm-6 form-label mb-1">{{ $t(`enum.balconyType.${order?.balconyType}`) }}</label>
    </div>
    <div class="row1">
      <label class="col-sm-6 form-label text-secondary mb-1">{{ $t('components.product-parameter.balcony.mounting-type') }}</label>
      <label class="col-sm-6 form-label mb-1">{{ $t(`enum.mountingType.${order?.mountingType}`) }}</label>
    </div>
    <div class="row1">
      <label class="col-sm-6 form-label text-secondary mb-1">{{ $t('components.product-parameter.balcony.paneel-type') }}</label>
      <label class="col-sm-6 form-label mb-1">{{ $t(`enum.paneelType.${order?.paneelType}`) }}</label>
    </div>
    <div class="row1">
      <label class="col-sm-6 form-label text-secondary mb-1">{{ $t('components.order-form.width') }}</label>
      <label class="col-sm-6 form-label mb-1">{{ order?.calfCircumferenceWithoutDeduction }}</label>
    </div>
    <div class="row1">
      <label class="col-sm-6 form-label text-secondary mb-1">{{ $t('components.order-form.height') }}</label>
      <label class="col-sm-6 form-label mb-1">{{ order?.heightWithoutDeduction }}</label>
    </div>
    <div class="row1">
      <label class="col-sm-6 form-label text-secondary mb-1">{{ $t('components.order-form.depth') }}</label>
      <label class="col-sm-6 form-label mb-1">{{ order?.depth }}</label>
    </div>
  </div>
</template>

<script lang="ts">
import { BalconyType, MountingType, PaneelType, OrientationType } from '@/types/enum/order';
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { Check, Close, Connection } from '@element-plus/icons-vue';
import { shallowRef } from 'vue';
import { OrderData } from '@/views/ConfiguratorPage.vue';

@Component({
  components: {},
  emits: ['sizeChanged'],
})
@Component
export default class ProductParameterFenceSun extends Vue {
  @Prop() order!: OrderData;
  @Prop({ default: false }) readonly!: boolean;

  BalconyType = BalconyType;
  MountingType = MountingType;
  PaneelType = PaneelType;
  OrientationType = OrientationType;

  depthConnected = false;

  Check = shallowRef(Check);
  Close = shallowRef(Close);
  Connection = shallowRef(Connection);

  mountingTypeChanged(): void {
    if (this.order?.paneelType == PaneelType.portrait && this.order) {
      this.order.mountingType = MountingType.front;
    }

    if (this.order?.mountingType == MountingType.front && this.order?.paneelType == PaneelType.landscape) {
      this.order.heightWithoutDeduction = 1015;
    } else if (this.order?.mountingType == MountingType.bottom && this.order?.paneelType == PaneelType.landscape) {
      this.order.heightWithoutDeduction = 1020;
    } else if (this.order?.mountingType == MountingType.front && this.order?.paneelType == PaneelType.portrait) {
      this.order.heightWithoutDeduction = 1645;
    }
  }

  getImageSrc(type: string) {
    const images = import.meta.glob('@/assets/images/balcony/*.png', { eager: true });
    return (images[`/src/assets/images/balcony/${type}.png`] as any)?.default || '';
  }
}
</script>

<style scoped lang="scss">
.text-secondary {
  color: #0000008d !important;
  text-align: left !important;
}

.row1 {
  border-bottom: 0.1rem solid #6767678d;
}

.form-label {
  font-size: 1rem;
  margin: 0.7rem 0 0.5rem 0;
  text-align: right;
}

.parameter-img {
  padding-top: 12px;
  width: 100px;
  height: 100px;
}
</style>
